import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useApolloClient } from '@apollo/client/react';
import {
  Button,
  Progress,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import {
  UtensilsCrossed,
  Building2,
  ShoppingBag,
  MapPin,
  ShieldAlert,
  Film,
  Store,
  LayoutGrid,
  GraduationCap,
  DollarSign,
  Landmark,
  ShoppingCart,
  Dumbbell,
  HeartPulse,
  Hotel,
  Home,
  Factory,
  Megaphone,
  Church,
  Truck,
  Users,
  Globe,
  RefreshCw,
  HelpCircle,
  ArrowLeft,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import {
  UPDATE_USER_SETTINGS_AUTH,
  GET_USER_SETTINGS_AUTH,
  SUBMIT_USER_SURVEY,
} from '@scio3/graphql-client';
import type { SubmitUserSurveyVariables } from '@scio3/graphql-client';
import { SURVEY_FORM_TYPES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';

// ─── Types ───────────────────────────────────────────────────

type Step = 'q1' | 'q2' | 'q3' | 'q4';

interface CardOption {
  id: string;
  icon: LucideIcon;
  label: string;
  description: string;
}

interface ChipOptionData {
  id: string;
  icon?: LucideIcon;
  label: string;
}

// ─── Step data ───────────────────────────────────────────────

// Q1: What are you planning on using OptiSigns for?
const USE_CASE_OPTIONS: CardOption[] = [
  {
    id: 'menu-boards',
    icon: UtensilsCrossed,
    label: 'Menu Boards',
    description: 'Digital menus for restaurants, cafes, and food service',
  },
  {
    id: 'corporate-comms',
    icon: Building2,
    label: 'Corporate Communications',
    description: 'Internal messaging, KPIs, and employee engagement',
  },
  {
    id: 'retail-displays',
    icon: ShoppingBag,
    label: 'Retail Displays',
    description: 'Product promotions, pricing, and in-store advertising',
  },
  {
    id: 'wayfinding',
    icon: MapPin,
    label: 'Wayfinding',
    description: 'Directories, navigation, and visitor information',
  },
  {
    id: 'emergency-alerts',
    icon: ShieldAlert,
    label: 'Emergency Alerts',
    description: 'Safety notifications and critical announcements',
  },
  {
    id: 'entertainment',
    icon: Film,
    label: 'Entertainment',
    description: 'Video content, social feeds, and customer engagement',
  },
];

// Q2: What is your industry?
const INDUSTRY_OPTIONS: ChipOptionData[] = [
  { id: 'c-stores', icon: Store, label: 'C-Stores' },
  { id: 'corporate-office', icon: LayoutGrid, label: 'Corporate Office' },
  { id: 'education', icon: GraduationCap, label: 'Education' },
  { id: 'finance', icon: DollarSign, label: 'Finance' },
  { id: 'government', icon: Landmark, label: 'Government' },
  { id: 'grocery', icon: ShoppingCart, label: 'Grocery' },
  { id: 'fitness-centers', icon: Dumbbell, label: 'Fitness Centers' },
  { id: 'healthcare', icon: HeartPulse, label: 'Healthcare' },
  { id: 'hospitality', icon: Hotel, label: 'Hospitality' },
  { id: 'property-management', icon: Home, label: 'Property Management' },
  { id: 'manufacturing', icon: Factory, label: 'Manufacturing' },
  { id: 'marketing', icon: Megaphone, label: 'Marketing' },
  { id: 'religious', icon: Church, label: 'Religious Organizations' },
  { id: 'restaurants', icon: UtensilsCrossed, label: 'Restaurants' },
  { id: 'retail', icon: ShoppingBag, label: 'Retail' },
  { id: 'transportation', icon: Truck, label: 'Transportation and Logistics' },
  { id: 'other', label: 'Other' },
];

// Q3: Who is your primary audience?
const AUDIENCE_OPTIONS: CardOption[] = [
  {
    id: 'internal',
    icon: Users,
    label: 'Internal',
    description: 'Employees, staff, and internal stakeholders',
  },
  {
    id: 'external',
    icon: Globe,
    label: 'External',
    description: 'Customers, visitors, and general public',
  },
  {
    id: 'both',
    icon: RefreshCw,
    label: 'Both',
    description: 'Mix of internal and external audiences',
  },
  {
    id: 'other',
    icon: HelpCircle,
    label: 'Other',
    description: 'Different or specialized audience type',
  },
];

// Q4: How many screens will you use?
const SCREEN_COUNT_OPTIONS = ['1-10', '11-25', '26-100', '100+'] as const;

// Form metadata — used when submitting answers to the backend
const FORM_ID = 'account_onboarding';
const FORM_VERSION = 1;

// ─── Step illustrations (white-on-green SVGs) ────────────────

const STEP_IMAGES: Partial<Record<Step, string>> = {
  q1: '/onboarding/screens-illustration.png',
  q2: 'https://cdn.prod.website-files.com/638d465c7903a622e8a1dbd1/65282f4b5ea3db40ebb0cccd_HIW_hero.svg',
  q3: 'https://cdn.prod.website-files.com/638ec3d4fcf5e083b0519cf8/65268880f78886f293db4662_lobby_hero.svg',
};

function StepIllustration({ step }: { step: Step }) {
  const src = STEP_IMAGES[step];

  if (src) {
    return (
      <div className="flex h-full w-full items-center justify-center px-20 py-12">
        <img src={src} alt="" className="max-h-full max-w-full object-contain" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-12">
      <div className="flex aspect-square w-full max-w-[400px] items-center justify-center rounded-xl border-2 border-dashed border-white/30">
        <span className="text-lg font-medium text-white/50">{step} — 1200 × 1200</span>
      </div>
    </div>
  );
}

// ─── Selection card component ────────────────────────────────

function SelectionCard({
  icon: Icon,
  label,
  description,
  isSelected,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex cursor-pointer flex-col items-center gap-2 rounded-lg text-center transition-colors',
        isSelected
          ? 'border-2 border-secondary-foreground bg-secondary-foreground/10 p-[23px]'
          : 'border border-muted-foreground/25 bg-background p-6 hover:bg-accent',
      )}
    >
      <Icon
        className={cn(
          'h-6 w-6',
          isSelected ? 'text-secondary-foreground' : 'text-muted-foreground',
        )}
      />
      <span className="text-sm font-semibold text-foreground">{label}</span>
      {description && <span className="text-xs text-muted-foreground">{description}</span>}
    </button>
  );
}

// ─── Chip/pill option (with optional icon) ──────────────────

function ChipOption({
  icon: Icon,
  label,
  isSelected,
  onClick,
}: {
  icon?: LucideIcon;
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-md text-sm font-medium transition-colors',
        isSelected
          ? 'border-2 border-secondary-foreground bg-secondary-foreground/10 px-[19px] py-[7px] text-foreground'
          : 'border border-muted-foreground/25 bg-background px-5 py-2 text-foreground hover:bg-accent',
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {label}
    </button>
  );
}

// ─── Shared step layout ──────────────────────────────────────

function StepContent({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// ─── Main onboarding form ────────────────────────────────────

interface OnboardingFormProps {
  /** Called after the user completes or skips the form. When rendered from
   *  AppShell this is not needed (Apollo cache re-render handles dismissal).
   *  When rendered from SignupPage this navigates to the app. */
  onComplete?: () => void;
}

export function OnboardingForm({ onComplete }: OnboardingFormProps = {}) {
  const { t } = useTranslation();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const [step, setStep] = useState<Step>('q1');
  const [useCase, setUseCase] = useState<string | null>(null);
  const [industry, setIndustry] = useState<string | null>(null);
  const [audience, setAudience] = useState<string | null>(null);
  const [screenCount, setScreenCount] = useState<string | null>(null);

  const client = useApolloClient();
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  const [updateSettings] = useMutation(UPDATE_USER_SETTINGS_AUTH, {
    refetchQueries: [{ query: GET_USER_SETTINGS_AUTH, variables: { teamId: activeTeamId } }],
  });

  const [submitSurvey] = useMutation<unknown, SubmitUserSurveyVariables>(SUBMIT_USER_SURVEY);

  const stepOrder: Step[] = ['q1', 'q2', 'q3', 'q4'];

  const questionNumber = stepOrder.indexOf(step) + 1;
  const totalQuestions = stepOrder.length;
  const stepIndex = stepOrder.indexOf(step);
  const progressPercent = (questionNumber / totalQuestions) * 100;

  const canContinue = (() => {
    switch (step) {
      case 'q1':
        return useCase !== null;
      case 'q2':
        return industry !== null;
      case 'q3':
        return audience !== null;
      case 'q4':
        return screenCount !== null;
      default:
        return false;
    }
  })();

  const isLastStep = step === 'q4';

  /**
   * Build the answer payload and submit to backend.
   * Each answer includes the question text + the selected value/label
   * so the response is self-describing (snapshot pattern).
   */
  async function handleComplete() {
    setSubmitting(true);

    // Build the questions array — snapshot of what the user answered
    const questions: SubmitUserSurveyVariables['input']['questions'] = [];

    if (useCase) {
      const opt = USE_CASE_OPTIONS.find((o) => o.id === useCase);
      questions.push({
        key: 'use_case',
        text: 'What are you planning on using OptiSigns for?',
        answer: useCase,
        answerLabel: opt?.label ?? useCase,
      });
    }

    if (industry) {
      const opt = INDUSTRY_OPTIONS.find((o) => o.id === industry);
      questions.push({
        key: 'industry',
        text: 'What is your industry?',
        answer: industry,
        answerLabel: opt?.label ?? industry,
      });
    }

    if (audience) {
      const opt = AUDIENCE_OPTIONS.find((o) => o.id === audience);
      questions.push({
        key: 'audience',
        text: 'Who is your primary audience?',
        answer: audience,
        answerLabel: opt?.label ?? audience,
      });
    }

    if (screenCount) {
      questions.push({
        key: 'screen_count',
        text: 'How many screens will you use?',
        answer: screenCount,
        answerLabel: screenCount,
      });
    }

    try {
      // Submit survey answers to backend (also sets onboardFormSubmitted=true)
      await submitSurvey({
        variables: {
          input: {
            formId: FORM_ID,
            formVersion: FORM_VERSION,
            formType: SURVEY_FORM_TYPES.ACCOUNT_ONBOARDING,
            questions,
          },
        },
      });

      // Mark onboarding as completed in backend
      await updateSettings({
        variables: {
          updateData: { onboardFormSubmitted: true },
          teamId: activeTeamId,
        },
      });

      // Refetch the exact query useShouldShowOnboarding reads (no variables)
      // so AppShell's cache is up-to-date before we navigate — prevents Q1 flash.
      await client.query({ query: GET_USER_SETTINGS_AUTH, fetchPolicy: 'network-only' });

      useAuthStore.getState().setPendingOnboarding(false);
      onComplete?.();
    } catch (error) {
      console.error('Failed to save onboarding survey:', error);
      // Still try to dismiss the form even if survey save fails
      try {
        await updateSettings({
          variables: {
            updateData: { onboardFormSubmitted: true },
            teamId: activeTeamId,
          },
        });
        await client.query({ query: GET_USER_SETTINGS_AUTH, fetchPolicy: 'network-only' });
        useAuthStore.getState().setPendingOnboarding(false);
        onComplete?.();
      } catch {
        console.error('Failed to dismiss onboarding form');
        setSubmitting(false);
        setSubmitFailed(true);
      }
    }
  }

  function handleForceSkip() {
    useAuthStore.getState().setPendingOnboarding(false);
    onComplete?.();
  }

  function handleNext() {
    if (isLastStep) {
      handleComplete();
      return;
    }
    const nextIndex = stepIndex + 1;
    if (nextIndex < stepOrder.length) {
      setStep(stepOrder[nextIndex]);
    }
  }

  function handleBack() {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setStep(stepOrder[prevIndex]);
    }
  }

  function getButtonLabel() {
    if (isLastStep) return t('Take me to OptiSigns');
    return t('Continue');
  }

  if (submitting) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-background">
        <Progress value={100} className="h-2 shrink-0 rounded-none border-b border-white" />
        <div className="flex min-h-0 flex-1">
          <div style={{ flex: '1 1 0%' }} className="flex h-full items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="text-sm text-muted-foreground">{t('Setting up your account...')}</p>
            </div>
          </div>
          <div
            style={{ flex: '1 1 0%' }}
            className="relative hidden bg-primary lg:flex lg:items-center lg:justify-center"
          >
            <StepIllustration step={step} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-background">
      {/* Progress bar — spans full width, white border so it's visible on green panel */}
      <Progress
        value={progressPercent}
        className="h-2 shrink-0 rounded-none border-b border-white"
      />

      <div className="flex min-h-0 flex-1">
        {/* Left panel — form content, scrollable */}
        <div style={{ flex: '1 1 0%' }} className="flex h-full flex-col overflow-y-auto">
          <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-between px-5 py-32 sm:px-8">
            <div>
              {/* Logo — matches site nav text style */}
              <span className="mb-8 inline-block text-lg font-bold text-primary">OptiSigns</span>

              {/* Q1: What are you planning on using OptiSigns for? */}
              {step === 'q1' && (
                <StepContent title={t('What are you planning on using OptiSigns for?')}>
                  <div className="grid grid-cols-2 gap-4">
                    {USE_CASE_OPTIONS.map((opt) => (
                      <SelectionCard
                        key={opt.id}
                        icon={opt.icon}
                        label={t(opt.label)}
                        description={t(opt.description)}
                        isSelected={useCase === opt.id}
                        onClick={() => setUseCase(opt.id)}
                      />
                    ))}
                  </div>
                </StepContent>
              )}

              {/* Q2: What is your industry? */}
              {step === 'q2' && (
                <StepContent title={t('What is your industry?')} subtitle="">
                  <div className="flex flex-wrap gap-3">
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <ChipOption
                        key={opt.id}
                        icon={opt.icon}
                        label={t(opt.label)}
                        isSelected={industry === opt.id}
                        onClick={() => setIndustry(opt.id)}
                      />
                    ))}
                  </div>
                </StepContent>
              )}

              {/* Q3: Who is your primary audience? */}
              {step === 'q3' && (
                <StepContent title={t('Who is your primary audience?')}>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {AUDIENCE_OPTIONS.map((opt) => (
                      <SelectionCard
                        key={opt.id}
                        icon={opt.icon}
                        label={t(opt.label)}
                        description={t(opt.description)}
                        isSelected={audience === opt.id}
                        onClick={() => setAudience(opt.id)}
                      />
                    ))}
                  </div>
                </StepContent>
              )}

              {/* Q4: How many screens will you use? */}
              {step === 'q4' && (
                <StepContent title={t('How many screens will you use?')}>
                  <div className="flex flex-wrap gap-3">
                    {SCREEN_COUNT_OPTIONS.map((opt) => (
                      <ChipOption
                        key={opt}
                        label={opt}
                        isSelected={screenCount === opt}
                        onClick={() => setScreenCount(opt)}
                      />
                    ))}
                  </div>
                </StepContent>
              )}
            </div>

            {/* Navigation buttons — pinned to bottom, back left / continue right */}
            {submitFailed && isLastStep && (
              <p className="pt-4 text-center text-sm text-muted-foreground">
                {t('Something went wrong.')}{' '}
                <button
                  type="button"
                  onClick={handleForceSkip}
                  className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {t('Skip and continue to OptiSigns')}
                </button>
              </p>
            )}
            <div className="flex shrink-0 items-center justify-between pt-6">
              {step !== 'q1' ? (
                <Button variant="outline" size="lg" onClick={handleBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('Back')}
                </Button>
              ) : (
                <div />
              )}
              <TooltipProvider delayDuration={0}>
                <Tooltip open={!canContinue ? undefined : false}>
                  <TooltipTrigger asChild>
                    {/* Wrapper span so tooltip works on disabled button (disabled elements don't fire pointer events) */}
                    <span tabIndex={!canContinue ? 0 : undefined}>
                      <Button
                        size="lg"
                        onClick={handleNext}
                        disabled={!canContinue}
                        className={cn(!canContinue && 'pointer-events-none')}
                      >
                        {getButtonLabel()}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="z-[101]">
                    {t('Make a selection to continue')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>

        {/* Right panel — matches AuthLayout: hidden below lg (1024px), bg-primary */}
        <div
          style={{ flex: '1 1 0%' }}
          className="relative hidden bg-primary lg:flex lg:items-center lg:justify-center"
        >
          <StepIllustration step={step} />
        </div>
      </div>
    </div>
  );
}
